import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Shield, Server, Cookie, FileText, Lock, Mail, 
  CreditCard, Globe, Database, Eye, AlertTriangle,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';

const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Shield className="h-4 w-4" />
            DSGVO-konform
          </div>
          <h1 className="font-display text-4xl font-bold mb-4" data-testid="datenschutz-title">
            Datenschutzerklärung
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Transparenz und Schutz deiner persönlichen Daten sind uns wichtig.
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Section 1 - Überblick */}
          <Card data-testid="section-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                1. Datenschutz auf einen Blick
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="font-semibold mb-2">Allgemeine Hinweise</p>
                <p className="text-muted-foreground">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit deinen personenbezogenen Daten 
                  passiert, wenn du diese Website besuchst. Personenbezogene Daten sind alle Daten, mit denen du 
                  persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen 
                  du unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>
              </div>
              
              <div>
                <p className="font-semibold text-foreground mb-3">Datenerfassung auf dieser Website</p>
                <div className="space-y-3">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-semibold text-sm mb-1">Wer ist verantwortlich für die Datenerfassung?</p>
                    <p className="text-xs text-muted-foreground">
                      Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
                      Dessen Kontaktdaten kannst du dem Impressum dieser Website entnehmen.
                    </p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-semibold text-sm mb-1">Wie erfassen wir deine Daten?</p>
                    <p className="text-xs text-muted-foreground">
                      deine Daten werden zum einen dadurch erhoben, dass du uns diese mitteilst (z. B. durch das Bestellformular 
                      oder die Newsletter-Anmeldung). Andere Daten werden automatisch oder nach deiner Einwilligung beim Besuch 
                      der Website durch unsere IT-Systeme erfasst (z. B. IP-Adresse, Browser, Betriebssystem).
                    </p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-semibold text-sm mb-1">Wofür nutzen wir deine Daten?</p>
                    <p className="text-xs text-muted-foreground">
                      Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                      Andere Daten können zur Analyse deines Nutzerverhaltens (Matomo) oder für den Versand von Informationen 
                      (Newsletter) verwendet werden.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2 - Hosting */}
          <Card data-testid="section-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                2. Hosting und Serverinfrastruktur (Eigene Hardware)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <p className="font-semibold text-green-700 dark:text-green-400 mb-2">
                  🇩🇪 100% Made in Germany - Eigene Hardware
                </p>
                <p className="text-green-600 dark:text-green-500 text-sm">
                  Wir hosten unsere Systeme und damit auch deine Daten <strong>nicht</strong> bei anonymen Cloud-Anbietern, 
                  sondern auf eigener Hardware (Dedicated Hardware).
                </p>
              </div>
              
              <div>
                <p className="font-semibold text-foreground mb-2">Colocation im Digital Realty Rechenzentrum (Frankfurt)</p>
                <p>
                  Unsere Server befinden sich in einem abgeschlossenen Sicherheitsbereich ("Cage") im Rechenzentrum FRA7 
                  der Digital Realty Germany GmbH, Lyoner Straße 9, 60528 Frankfurt am Main.
                </p>
              </div>
              
              <p>
                Wir haben mit dem Betreiber des Rechenzentrums einen Vertrag über die Auftragsverarbeitung (AVV) geschlossen. 
                Dieser stellt sicher, dass der Betreiber die physische Sicherheit der Server gewährleistet, aber keinen 
                logischen Zugriff auf deine gespeicherten Daten hat.
              </p>
              
              <p className="text-xs bg-muted/50 p-3 rounded-lg">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse an einer sicheren 
                und professionellen Bereitstellung unseres Online-Angebots) sowie Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung 
                gegenüber unseren Kunden).
              </p>
            </CardContent>
          </Card>

          {/* Section 3 - Pflichtinformationen */}
          <Card data-testid="section-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                3. Allgemeine Hinweise und Pflichtinformationen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground mb-2">Datenschutz</p>
                <p>
                  Die Betreiber dieser Seiten nehmen den Schutz deiner persönlichen Daten sehr ernst. 
                  Wir behandeln deine personenbezogenen Daten vertraulich und entsprechend der gesetzlichen 
                  Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                </p>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-semibold text-foreground mb-2">Verantwortliche Stelle</p>
                <p className="mb-1">Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:</p>
                <div className="mt-3 space-y-1">
                  <p className="font-medium">Host-On Service Provider GmbH</p>
                  <p>Hanauer Landstr. 204</p>
                  <p>60314 Frankfurt am Main</p>
                  <p className="mt-2">Telefon: 069 247 499 0</p>
                  <p>E-Mail: Support@Wolkenhelden.de</p>
                </div>
              </div>
              
              <div>
                <p className="font-semibold text-foreground mb-2">Widerruf deiner Einwilligung zur Datenverarbeitung</p>
                <p>
                  Viele Datenverarbeitungsvorgänge sind nur mit deiner ausdrücklichen Einwilligung möglich. 
                  du können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine formlose 
                  Mitteilung per E-Mail an uns. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung 
                  bleibt vom Widerruf unberührt.
                </p>
              </div>
              
              <div>
                <p className="font-semibold text-foreground mb-2">Beschwerderecht bei der zuständigen Aufsichtsbehörde</p>
                <p>
                  Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer 
                  Aufsichtsbehörde zu, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres 
                  Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes.
                </p>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <Lock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-blue-700 dark:text-blue-400">SSL- bzw. TLS-Verschlüsselung</p>
                  <p className="text-xs text-blue-600 dark:text-blue-500">
                    Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, 
                    wie zum Beispiel Bestellungen oder Anfragen, die du an uns als Seitenbetreiber senden, eine 
                    SSL- bzw. TLS-Verschlüsselung.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 4 - Cookies */}
          <Card data-testid="section-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-primary" />
                4. Datenerfassung auf dieser Website
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground mb-2">Cookies</p>
                <p>
                  Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Textdateien und 
                  richten auf deinem Endgerät keinen Schaden an. du werden entweder vorübergehend für die Dauer 
                  einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf deinem Endgerät gespeichert.
                </p>
              </div>
              
              <p className="font-medium text-foreground">Wir unterscheiden zwischen zwei Arten von Cookies:</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="font-semibold text-green-700 dark:text-green-400 text-sm mb-1">
                    1. Technisch notwendige Cookies
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-500">
                    Diese sind erforderlich, um den Betrieb der Website und des Kundenportals sicherzustellen 
                    (z. B. Warenkorb, Login-Session, CSRF-Security-Tokens). Rechtsgrundlage hierfür ist Art. 6 Abs. 1 
                    lit. f DSGVO (Berechtigtes Interesse an der fehlerfreien Bereitstellung der Dienste).
                  </p>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="font-semibold text-amber-700 dark:text-amber-400 text-sm mb-1">
                    2. Statistik- und Marketing-Cookies
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Diese werden nur mit deiner Einwilligung gesetzt (z. B. für die Matomo-Analyse). 
                    Rechtsgrundlage ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Diese Einwilligung können 
                    du jederzeit über unsere Cookie-Einstellungen widerrufen.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="font-semibold text-foreground mb-2">Server-Log-Dateien</p>
                <p className="mb-2">
                  Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten 
                  Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs bg-muted/30 p-3 rounded-lg">
                  <li>Browsertyp und Browserversion</li>
                  <li>Verwendetes Betriebssystem</li>
                  <li>Referrer URL</li>
                  <li>Hostname des zugreifenden Rechners</li>
                  <li>Uhrzeit der Serveranfrage</li>
                  <li>IP-Adresse (in anonymisierter Form, soweit technisch möglich)</li>
                </ul>
                <p className="text-xs mt-2">
                  Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen.
                </p>
              </div>
              
              <div>
                <p className="font-semibold text-foreground mb-2">Kontaktformular / Support-Tickets</p>
                <p>
                  Wenn du uns per Kontaktformular oder Ticket-System Anfragen zukommen lassen, werden deine Angaben 
                  aus dem Anfrageformular inklusive der von dir dort angegebenen Kontaktdaten zwecks Bearbeitung 
                  der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht 
                  ohne deine Einwilligung weiter.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 5 - Matomo */}
          <Card data-testid="section-5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                5. Analyse-Tools (Matomo)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                Diese Website benutzt den Open Source Webanalysedienst Matomo. Matomo verwendet Technologien, 
                die die seitenübergreifende Wiedererkennung des Nutzers zur Analyse des Nutzerverhaltens ermöglichen 
                (z. B. Cookies oder Device-Fingerprinting).
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Server className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground text-sm">Hosting auf eigenen Servern</p>
                    <p className="text-xs">
                      Wir hosten Matomo ausschließlich auf unseren eigenen Servern in Frankfurt am Main. 
                      Alle Analysedaten bleiben bei uns und werden nicht weitergegeben. Es findet keine 
                      Übertragung an Drittanbieter oder Server im Ausland statt.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground text-sm">IP-Anonymisierung</p>
                    <p className="text-xs">
                      Wir haben in Matomo die IP-Anonymisierung aktiviert. Dadurch wird deine IP-Adresse vor der 
                      Speicherung gekürzt. Die mittels Matomo erhobenen Informationen über die Benutzung dieser 
                      Website werden nicht an Dritte weitergegeben.
                    </p>
                  </div>
                </div>
              </div>
              
              <p className="text-xs bg-muted/50 p-3 rounded-lg">
                <strong>Rechtsgrundlage:</strong> Die Nutzung dieses Analyse-Tools erfolgt auf Grundlage von 
                Art. 6 Abs. 1 lit. a DSGVO; die Einwilligung ist jederzeit über unsere Cookie-Einstellungen widerrufbar.
              </p>
            </CardContent>
          </Card>

          {/* Section 6 - Newsletter */}
          <Card data-testid="section-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                6. Newsletter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground mb-2">Newsletterdaten</p>
                <p>
                  Wenn du den auf der Website angebotenen Newsletter beziehen möchten, benötigen wir von dir 
                  eine E-Mail-Adresse sowie Informationen, welche uns die Überprüfung gestatten, dass du der 
                  Inhaber der angegebenen E-Mail-Adresse sind und mit dem Empfang des Newsletters einverstanden 
                  sind (Double-Opt-In Verfahren). Weitere Daten werden nicht bzw. nur auf freiwilliger Basis erhoben. 
                  Diese Daten verwenden wir ausschließlich für den Versand der angeforderten Informationen und 
                  geben sie nicht an Dritte weiter.
                </p>
              </div>
              
              <p className="text-xs bg-muted/50 p-3 rounded-lg">
                <strong>Rechtsgrundlage:</strong> Die Verarbeitung der in das Newsletteranmeldeformular eingegebenen 
                Daten erfolgt ausschließlich auf Grundlage deiner Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). 
                Die erteilte Einwilligung zur Speicherung der Daten, der E-Mail-Adresse sowie deren Nutzung zum 
                Versand des Newsletters kannst du jederzeit widerrufen, etwa über den „Austragen"-Link im Newsletter.
              </p>
            </CardContent>
          </Card>

          {/* Section 7 - Zahlungsabwicklung */}
          <Card data-testid="section-7">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                7. Zahlungsabwicklung (Subscriptions)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                Zur Abwicklung der kostenpflichtigen Managed-Hosting-Verträge setzen wir Zahlungsdienstleister ein. 
                Die Verarbeitung der Zahlungsdaten erfolgt zur Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO).
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-[#0070ba] rounded flex items-center justify-center text-white font-bold text-xs">PP</div>
                    <p className="font-semibold text-blue-700 dark:text-blue-400">PayPal (Subscription / Recurring Payments)</p>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-500 mb-2">
                    Wir bieten die Bezahlung via PayPal an. Anbieter ist die PayPal (Europe) S.à.r.l. et Cie, S.C.A., 
                    22-24 Boulevard Royal, L-2449 Luxembourg.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-500 mb-2">
                    Wenn du diese Zahlungsart wählen und ein Abonnement abschließen, werden die von dir eingegebenen 
                    Zahlungsdaten an PayPal übermittelt. Durch die "Subscription"-Funktion ermächtigen du PayPal, 
                    die fälligen Beträge wiederkehrend von deinem Konto einzuziehen.
                  </p>
                  <a href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer" 
                     className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />
                    PayPal Datenschutzerklärung
                  </a>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-[#635bff] rounded flex items-center justify-center text-white font-bold text-xs">S</div>
                    <p className="font-semibold text-purple-700 dark:text-purple-400">Stripe (Kreditkarte / Subscription)</p>
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-500 mb-2">
                    Wir bieten die Zahlung per Kreditkarte über den Dienstleister Stripe an. Anbieter ist die 
                    Stripe Payments Europe, Ltd., 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, Irland.
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-500 mb-2">
                    Bei Zahlung via Stripe werden deine Zahlungsdaten (z. B. Kreditkartennummer, Gültigkeit, CVC) 
                    direkt an Stripe übermittelt. Wir selbst speichern keine Kreditkartendaten. Stripe wickelt die 
                    wiederkehrenden Zahlungen (Subscriptions) in unserem Auftrag ab.
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-500 mb-2">
                    Stripe setzt unter Umständen Cookies zur Betrugserkennung und Gewährleistung der Sicherheit der Transaktion ein.
                  </p>
                  <a href="https://stripe.com/de/privacy" target="_blank" rel="noopener noreferrer" 
                     className="text-xs text-purple-600 hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />
                    Stripe Datenschutzerklärung
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 8 - Domain-Registrierung */}
          <Card data-testid="section-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                8. Domain-Registrierung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                Sofern du über uns eine Domain registrieren, geben wir die hierfür erforderlichen Daten 
                (Name, Adresse, E-Mail) an unseren Domain-Großhändler (Registrar) und die jeweilige Vergabestelle 
                (Registry, z. B. DENIC für .de) weiter.
              </p>
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-semibold text-foreground mb-2">InterNetX</p>
                <p>
                  Unser technischer Partner für die Domainregistrierung ist die InterNetX GmbH, 
                  Johanna-Dachs-Str. 55, 93055 Regensburg, Deutschland.
                </p>
              </div>
              
              <p className="text-xs bg-muted/50 p-3 rounded-lg">
                <strong>Rechtsgrundlage:</strong> Die Weitergabe der Daten ist zwingend erforderlich, 
                um den Registrierungsvertrag für die Domain zu erfüllen (Art. 6 Abs. 1 lit. b DSGVO) 
                und entspricht den Vergaberichtlinien der jeweiligen Domain-Endungen (ICANN / DENIC Richtlinien).
              </p>
            </CardContent>
          </Card>

          {/* Section 9 - Nextcloud Auftragsverarbeitung */}
          <Card data-testid="section-9">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                9. Verarbeitung von Daten in der Nextcloud (Auftragsverarbeitung)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Wichtiger Hinweis</p>
                    <p className="text-sm text-amber-600 dark:text-amber-500">
                      Wir weisen darauf hin, dass wir für die Daten, die du <strong>innerhalb</strong> deiner gebuchten 
                      Managed Nextcloud Instanz speichern (z. B. Dateien, Kontakte, Fotos, Kalender), als <strong>Auftragsverarbeiter</strong> tätig werden.
                    </p>
                  </div>
                </div>
              </div>
              
              <p>
                Diese Verarbeitung wird durch einen separaten <strong>Vertrag zur Auftragsverarbeitung (AVV)</strong> geregelt, 
                den wir dir bei Vertragsschluss zur Verfügung stellen. Darin garantieren wir die Sicherheit deiner Daten 
                und die Einhaltung der DSGVO-Vorgaben für Hosting-Dienstleister.
              </p>
              
              <Button asChild variant="outline">
                <Link to="/avv">
                  <Shield className="mr-2 h-4 w-4" />
                  Zum AVV
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Related Links */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <p className="font-semibold mb-4">Weitere rechtliche Dokumente:</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/agb">
                    <FileText className="mr-2 h-4 w-4" />
                    AGB
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/sla">
                    <Shield className="mr-2 h-4 w-4" />
                    SLA
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/avv">
                    <Lock className="mr-2 h-4 w-4" />
                    AVV
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/impressum">
                    <FileText className="mr-2 h-4 w-4" />
                    Impressum
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export { DatenschutzPage };
export default DatenschutzPage;
